<template>
  <b-modal
    v-model="show"
    title="Solicitud de Venta de Días Libres"
    modal-class="modal-success"
    size="lg"
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <b-overlay :show="loading" rounded="sm">
      <div v-if="conductor" class="p-1 mb-2 bg-light rounded">
        <h5 class="mb-50 font-weight-bold text-success">
          {{ conductor.empleado }}
        </h5>
        <div class="text-muted small">
          Documento: <strong>{{ conductor.documento }}</strong> ·
          Sueldo Principal: <strong>S/. {{ formatCurrency(conductor.sueldo) }}</strong>
          <span v-if="conductor.doble_planilla">
            · Sueldo Adicional: <strong>S/. {{ formatCurrency(conductor.sueldo_adicional) }}</strong>
          </span>
          · Saldo acumulado:
          <b-badge :variant="conductor.saldo_pendiente < 0 ? 'light-danger' : 'light-success'">
            {{ conductor.saldo_pendiente }} días
          </b-badge>
        </div>
      </div>

      <b-row>
        <b-col md="6">
          <b-form-group label="Seleccionar fecha(s) a vender:" label-class="font-weight-bold">
            <div class="d-flex mb-1">
              <b-form-input
                v-model="nuevaFecha"
                type="date"
                size="sm"
                class="mr-50"
              />
              <b-button variant="outline-success" size="sm" @click="agregarFecha">
                <feather-icon icon="PlusIcon" /> Agregar
              </b-button>
            </div>

            <div v-if="fechasSeleccionadas.length > 0" class="border rounded p-1" style="max-height: 160px; overflow-y: auto;">
              <div
                v-for="(f, idx) in fechasSeleccionadas"
                :key="idx"
                class="d-flex justify-content-between align-items-center mb-50 pb-50 border-bottom"
              >
                <span>📅 {{ f }}</span>
                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon p-0"
                  @click="removerFecha(idx)"
                >
                  <feather-icon icon="Trash2Icon" size="14" />
                </b-button>
              </div>
            </div>
            <span v-else class="text-muted small">No hay fechas seleccionadas.</span>
          </b-form-group>

          <!-- Checkboxes si tiene doble sueldo -->
          <b-form-group
            v-if="cotizacion && cotizacion.tiene_doble_sueldo"
            label="Sueldos a incluir en la venta:"
            label-class="font-weight-bold"
          >
            <b-form-checkbox-group
              v-model="sueldosSeleccionados"
              stacked
              @change="recalcularCotizacion"
            >
              <b-form-checkbox value="principal">
                Sueldo Principal (S/. {{ formatCurrency(cotizacion.sueldo_principal) }})
              </b-form-checkbox>
              <b-form-checkbox value="adicional">
                Sueldo Adicional (S/. {{ formatCurrency(cotizacion.sueldo_adicional) }})
              </b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <div class="p-2 border rounded bg-light-success border-success text-center mb-1">
            <span class="small font-weight-bold text-muted d-block mb-50">RESUMEN DE VENTA</span>
            <div class="mb-50">
              <span class="text-muted">Días a compensar: </span>
              <strong class="h4 text-primary">{{ fechasSeleccionadas.length }}</strong>
            </div>
            <div class="mb-50">
              <span class="text-muted">Sueldo Base Aplicable: </span>
              <strong>S/. {{ formatCurrency(cotizacion ? cotizacion.sueldo_total : 0) }}</strong>
            </div>
            <div class="mt-1 pt-1 border-top border-success">
              <span class="small font-weight-bold text-muted d-block">MONTO TOTAL A GENERAR (CAJA):</span>
              <h3 class="mb-0 font-weight-bolder text-success">
                S/. {{ formatCurrency(cotizacion ? cotizacion.monto_total : 0) }}
              </h3>
              <small class="text-muted">Cálculo: (Sueldo / 30) × {{ fechasSeleccionadas.length }} día(s)</small>
            </div>
          </div>

          <b-form-group label="Comentario / Motivo (opcional):" label-class="font-weight-bold small">
            <b-form-textarea
              v-model="comentario"
              rows="2"
              placeholder="Ingrese alguna observación sobre la solicitud..."
              size="sm"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-overlay>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="show = false">
        Cancelar
      </b-button>
      <b-button
        variant="success"
        :disabled="loading || fechasSeleccionadas.length === 0 || !cotizacion"
        @click="enviarSolicitud"
      >
        <feather-icon icon="SendIcon" class="mr-50" />
        Generar Solicitud de Venta
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import DiasLibresService from "../service/dias-libres.service";

export default {
  name: "SolicitudVentaModal",
  data() {
    return {
      show: false,
      loading: false,
      conductor: null,
      nuevaFecha: "",
      fechasSeleccionadas: [],
      sueldosSeleccionados: ["principal", "adicional"],
      comentario: "",
      cotizacion: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
  },
  methods: {
    open(conductor) {
      this.conductor = conductor;
      this.fechasSeleccionadas = [];
      this.comentario = "";
      this.nuevaFecha = new Date().toISOString().split("T")[0];
      this.sueldosSeleccionados = conductor.doble_planilla
        ? ["principal", "adicional"]
        : ["principal"];
      this.show = true;
      this.recalcularCotizacion();
    },
    agregarFecha() {
      if (!this.nuevaFecha) return;
      if (!this.fechasSeleccionadas.includes(this.nuevaFecha)) {
        this.fechasSeleccionadas.push(this.nuevaFecha);
        this.fechasSeleccionadas.sort();
        this.recalcularCotizacion();
      }
    },
    removerFecha(idx) {
      this.fechasSeleccionadas.splice(idx, 1);
      this.recalcularCotizacion();
    },
    async recalcularCotizacion() {
      if (!this.conductor) return;
      if (this.fechasSeleccionadas.length === 0) {
        this.cotizacion = null;
        return;
      }
      this.loading = true;
      try {
        const { data } = await DiasLibresService.cotizar({
          id_personal: this.conductor.personal_id,
          fechas: this.fechasSeleccionadas,
          sueldos_seleccionados: this.sueldosSeleccionados,
        });
        this.cotizacion = data.data || data;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error al cotizar",
            text: error.response?.data?.message || "No se pudo calcular la cotización.",
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async enviarSolicitud() {
      if (this.fechasSeleccionadas.length === 0) return;
      this.loading = true;
      try {
        const user = (this.currentUser && (this.currentUser.id || this.currentUser.user_id))
          ? this.currentUser
          : (JSON.parse(localStorage.getItem("userData")) || {});
        const userId = user.id || user.user_id;

        const payload = {
          id_personal: this.conductor.personal_id,
          fechas: this.fechasSeleccionadas,
          sueldos_seleccionados: this.sueldosSeleccionados,
          id_usuario: userId,
          comentario: this.comentario,
        };

        const { data } = await DiasLibresService.solicitar(payload);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Solicitud Registrada",
            text: data.message || "La solicitud de venta quedó registrada pendiente de aprobación.",
            icon: "CheckCircleIcon",
            variant: "success",
          },
        });

        this.show = false;
        this.$emit("saved");
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: error.response?.data?.message || "No se pudo registrar la solicitud.",
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(val) {
      return Number(val || 0).toFixed(2);
    },
    onHidden() {
      this.conductor = null;
      this.fechasSeleccionadas = [];
      this.cotizacion = null;
    },
  },
};
</script>
